import React from 'react';

import { Loader, Select, Stack, Text, Title } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import usePipelines from 'hooks/glmFlows/usePipelines';
import useTags from 'hooks/glmFlows/useTags';

import { GLMKiloFlowTriggers, GLMKiloFormValues } from '../types';

interface IGLMKiloTriggerConfigProps {
  form: UseFormReturnType<GLMKiloFormValues, (values: GLMKiloFormValues) => GLMKiloFormValues>;
}

const GLMKiloTriggerConfig = ({ form }: IGLMKiloTriggerConfigProps): JSX.Element => {
  const { trigger } = form.values;
  const isPipelineStage = trigger === GLMKiloFlowTriggers.PIPELINE_STAGE_MOVEMENT;

  const { data: pipelines = [], isLoading: pipelinesLoading } = usePipelines(isPipelineStage);
  const { data: tags = [], isLoading: tagsLoading } = useTags() as {
    data: string[] | undefined;
    isLoading: boolean;
  };

  if (isPipelineStage) {
    const selectedPipeline = pipelines.find(p => p.id === form.values.tcPipelineId);
    const stageOptions = (selectedPipeline?.stages ?? []).map(s => ({ value: s.id, label: s.name }));

    return (
      <Stack sx={{ paddingTop: '20px' }}>
        <Title order={3}>Which pipeline and stage?</Title>
        <Text size={15}>The flow fires when a contact moves into this pipeline stage.</Text>
        {pipelinesLoading ? (
          <Loader size='sm' />
        ) : (
          <>
            <Select
              label='Pipeline'
              placeholder='Select a pipeline'
              data={pipelines.map(p => ({ value: p.id, label: p.name }))}
              value={form.values.tcPipelineId}
              onChange={value => {
                form.setFieldValue('tcPipelineId', value);
                form.setFieldValue('tcStageId', null);
              }}
              searchable
            />
            <Select
              label='Stage'
              placeholder={selectedPipeline ? 'Select a stage' : 'Select a pipeline first'}
              data={stageOptions}
              value={form.values.tcStageId}
              onChange={value => form.setFieldValue('tcStageId', value)}
              disabled={!selectedPipeline}
              searchable
            />
          </>
        )}
      </Stack>
    );
  }

  // TAG_ADDED
  return (
    <Stack sx={{ paddingTop: '20px' }}>
      <Title order={3}>Which tag?</Title>
      <Text size={15}>The flow fires when this tag is added to a contact in Gym Lead Machine.</Text>
      {tagsLoading ? (
        <Loader size='sm' />
      ) : (
        <Select
          label='Tag'
          placeholder='Select a tag'
          data={(tags ?? []).map(t => ({ value: t, label: t }))}
          value={form.values.tcTag}
          onChange={value => form.setFieldValue('tcTag', value)}
          searchable
        />
      )}
    </Stack>
  );
};

export default GLMKiloTriggerConfig;
